// src/components/ERDEditor/VersionControl.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  MdFolder,
  MdHistory,
  MdRefresh,
  MdAccessTime,
  MdFileDownload,
  MdVisibility
} from 'react-icons/md';

const VersionControl = ({
  username,
  selectedEnvironment,
  onVersionLoad,
  onSidebarClose,
  onPreviewVersion,
  getClassContext
}) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    // Parse the timestamp format: YYYYMMDD-HHMMSS
    const datePart = timestamp.slice(0, 8);
    const timePart = timestamp.slice(9, 15);
    
    // Note: We parse manually to avoid timezone issues instead of using ISO strings
    
    // Parse as local time to avoid timezone issues
    const year = parseInt(datePart.slice(0, 4));
    const month = parseInt(datePart.slice(4, 6)) - 1; // Month is 0-indexed
    const day = parseInt(datePart.slice(6, 8));
    const hour = parseInt(timePart.slice(0, 2));
    const minute = parseInt(timePart.slice(2, 4));
    const second = parseInt(timePart.slice(4, 6));
    
    const date = new Date(year, month, day, hour, minute, second);
    
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  // Extract environment from filename
  const extractEnvironmentFromFilename = (filename) => {
    const parts = filename.split('-');
    if (parts.length >= 3) {
      return parts.slice(2).join('-').replace('.json', '');
    }
    return 'unknown';
  };

  // Fetch versions for selected environment
  const fetchVersions = useCallback(async (environment) => {
    if (!environment || environment === 'None' || !username) {
      setVersions([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get class context if available
      const classContext = getClassContext ? getClassContext() : null;
      
      // Build headers
      const headers = {
        'Content-Type': 'application/json',
        'User-Role': 'Student',
        'Requesting-User': username
      };
      
      // Add class context headers if available
      if (classContext && classContext.class_id) {
        headers['Class-ID'] = classContext.class_id.toString();
      }
      if (classContext && classContext.class_code) {
        headers['Class-Code'] = classContext.class_code;
      }
      
      const response = await fetch(`/api/saved-works/${username}/${environment}`, {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setVersions(data.versions || []);
      } else if (response.status === 404) {
        setVersions([]);
      } else {
        throw new Error(`Failed to fetch versions: ${response.status}`);
      }
    } catch (err) {
      console.error('Error fetching versions:', err);
      setError('Failed to load versions');
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [username, getClassContext]);

  // Load specific version
  const handleVersionLoad = async (filename) => {
    setLoading(true);
    setError('');

    try {
      // Get class context if available
      const classContext = getClassContext ? getClassContext() : null;
      
      // Build headers
      const headers = {
        'Content-Type': 'application/json',
        'User-Role': 'Student',
        'Requesting-User': username
      };
      
      // Add class context headers if available
      if (classContext && classContext.class_id) {
        headers['Class-ID'] = classContext.class_id.toString();
      }
      if (classContext && classContext.class_code) {
        headers['Class-Code'] = classContext.class_code;
      }
      
      const response = await fetch(`/api/saved-works/${username}/${selectedEnvironment}/${filename}`, {
        method: 'GET',
        headers,
        credentials: 'include'
      });

      if (response.ok) {
        const workData = await response.json();

        // Just call the parent handler - no more state management here
        onVersionLoad(workData);

        if (onSidebarClose) {
          onSidebarClose();
        }
      } else {
        throw new Error(`Failed to load version: ${response.status}`);
      }
    } catch (err) {
      console.error('Error loading version:', err);
      setError('Failed to load version');
    } finally {
      setLoading(false);
    }
  };



  // Refresh versions
  const handleRefresh = () => {
    fetchVersions(selectedEnvironment);
  };


  // Fetch versions when environment changes
  useEffect(() => {
    fetchVersions(selectedEnvironment);
  }, [selectedEnvironment, fetchVersions]);

  return (
    <div className="h-full flex flex-col space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-700 flex items-center">
          <MdHistory className="w-5 h-5 mr-2" />
          Version Control
        </h3>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="text-gray-400 hover:text-gray-600 disabled:opacity-50 p-2 rounded-md hover:bg-gray-100"
          title="Refresh versions"
        >
          <MdRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Environment Display */}
      {selectedEnvironment && selectedEnvironment !== 'None' && (
        <div className="space-y-3">
          <div className="flex items-center text-sm text-gray-600">
            <MdFolder className="w-4 h-4 mr-2" />
            Environment: <span className="font-medium ml-1">{selectedEnvironment}</span>
          </div>
        </div>
      )}

      {/* None Environment Message */}
      {selectedEnvironment === 'None' && (
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 text-center max-w-sm">
            <MdFolder className="w-12 h-12 text-orange-400 mx-auto mb-3" />
            <h4 className="text-lg font-medium text-orange-800 mb-2">
              No Environment Selected
            </h4>
            <p className="text-sm text-orange-600 mb-4">
              You have selected "None" as your environment. Version history is not available and your work will not be saved.
            </p>
            <p className="text-xs text-orange-500">
              Please select a valid environment from either dropdown to enable auto-save.
            </p>
          </div>
        </div>
      )}

      {/* Version Selection */}
      {selectedEnvironment && selectedEnvironment !== 'None' && (
        <div className="flex-1 flex flex-col space-y-3">
          <label className="block text-sm font-medium text-gray-700 flex items-center">
            <MdAccessTime className="w-4 h-4 mr-2" />
            Saved Versions ({versions.length})
          </label>

          {loading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-3">Loading versions...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && versions.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <MdAccessTime className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  No saved versions found for this environment
                </p>
              </div>
            </div>
          )}

          {!loading && !error && versions.length > 0 && (
            <div className="flex-1 overflow-y-auto space-y-2">
              {versions.map((version) => (
                <div
                  key={version.filename}
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-lg group hover:border-blue-300 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 text-base">
                        {formatTimestamp(version.filename.split('-').slice(0, 2).join('-') + '-' + version.filename.split('-')[2].split('.')[0])}
                      </p>
                      <p className="text-gray-500 text-sm mt-1">
                        Environment: {extractEnvironmentFromFilename(version.filename)}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onPreviewVersion(version.filename);
                        }}
                        className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-100 rounded-md transition-colors"
                        title="Preview version"
                      >
                        <MdVisibility className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVersionLoad(version.filename);
                        }}
                        className="p-2 text-green-500 hover:text-green-700 hover:bg-green-100 rounded-md transition-colors"
                        title="Load version"
                      >
                        <MdFileDownload className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default VersionControl;